/**
 * 🔒 Safe Storage Utility
 * Provides safe wrappers for localStorage and sessionStorage that:
 * 1. Safely handle Server-Side Rendering (SSR) without crashes.
 * 2. Gracefully handle browser security exceptions (e.g., blocked cookies/storage).
 * 3. Provide type-safe JSON serialization/deserialization.
 */

export class SafeStorage {
  /**
   * Checks if we are running in the browser and storage is accessible
   */
  private static isAvailable(type: 'localStorage' | 'sessionStorage'): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Safely gets an item from localStorage
   */
  static getItem(
    key: string,
    defaultValue: string | null = null,
  ): string | null {
    if (!this.isAvailable('localStorage')) return defaultValue;
    try {
      return localStorage.getItem(key) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Safely sets an item in localStorage
   */
  static setItem(key: string, value: string): boolean {
    if (!this.isAvailable('localStorage')) return false;
    try {
      localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Safely removes an item from localStorage
   */
  static removeItem(key: string): boolean {
    if (!this.isAvailable('localStorage')) return false;
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Safely gets and parses JSON from localStorage
   */
  static getJson<T>(key: string, defaultValue: T | null = null): T | null {
    const raw = this.getItem(key);
    if (!raw) return defaultValue;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Safely serializes and sets JSON in localStorage
   */
  static setJson<T>(key: string, value: T): boolean {
    try {
      return this.setItem(key, JSON.stringify(value));
    } catch {
      return false;
    }
  }

  /**
   * Safely gets an item from sessionStorage
   */
  static getSessionItem(
    key: string,
    defaultValue: string | null = null,
  ): string | null {
    if (!this.isAvailable('sessionStorage')) return defaultValue;
    try {
      return sessionStorage.getItem(key) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  }

  /**
   * Safely sets an item in sessionStorage
   */
  static setSessionItem(key: string, value: string): boolean {
    if (!this.isAvailable('sessionStorage')) return false;
    try {
      sessionStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Safely removes an item from sessionStorage
   */
  static removeSessionItem(key: string): boolean {
    if (!this.isAvailable('sessionStorage')) return false;
    try {
      sessionStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Safely clears localStorage except for preserved keys
   */
  static clear(preserveKeys: string[] = []): void {
    if (!this.isAvailable('localStorage')) return;
    try {
      if (preserveKeys.length === 0) {
        localStorage.clear();
        return;
      }

      // Preserve specific keys
      const preserved: Record<string, string> = {};
      for (const key of preserveKeys) {
        const val = localStorage.getItem(key);
        if (val !== null) {
          preserved[key] = val;
        }
      }

      localStorage.clear();

      for (const [key, val] of Object.entries(preserved)) {
        localStorage.setItem(key, val);
      }
    } catch {
      // If clear fails, try manual removal of other keys if possible
    }
  }

  /**
   * Safely clears sessionStorage
   */
  static clearSession(): void {
    if (!this.isAvailable('sessionStorage')) return;
    try {
      sessionStorage.clear();
    } catch {}
  }
}
